// 組織與場域（SPEC #org-tree-ui 第 2 點、o06 / o08 / o19 / o21 / o26 / o30 / o31；VMS UX 評審 2026-10-13 P1-7、B-25）。
//   左：結構樹（樹頂一行「數字 = 這一層以下的設備總數（含你沒有影像權的）」；家戶的核取方塊平常不顯示，按〔批次移動 / 指定管理人〕進選取模式才出現）。
//   右：選取的節點——路徑、結構總數、〔＋ 子節點〕〔＋ 戶〕〔改名 / 層級〕〔移動〕，下面三個分頁（網址 ?tab=）：
//     設備（這個節點以下的設備，〔在影像牆開啟〕）｜誰能存取（存取權統一清單篩這個節點，〔＋ 給存取權〕預先選好節點）｜
//     場域設定（名稱與層級、時區、地址與座標、斷網可用與本地 VMS 電腦；最下方「維護」區：中斷繼承、停用）。
//   層級名稱設定在「組織設定」（/org）。
//   權限：owner / org_admin 全部；org.sites 不能開關中斷繼承、不能指派；node_admin 只看到伺服器回的有效子樹（不能停用、中斷繼承、指定管理人）。
//   範圍固定為一個組織（頁首範圍所在的組織，SitesPage 的 OrgGate）。
//   不在選取模式時點樹上的戶 → 右側換成這一戶的詳情（HomeDetail：最下方「維護」區〔刪除這個戶〕，#org-tree-ui-home-delete）。
import { useMemo, useState } from 'react';
import { useScope } from '../../lib/scope';
import { nodePath, type ScopeNode } from '../../lib/myDevices';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { isKinioError } from '@kinio/web-sdk';
import { useConfirm } from '../../components/ConfirmDialog';
import { hasOrgPerm, isOrgAdmin, type MyOrg } from '../../lib/planeApi';
import { treeApi, type Assignment, type OrgTreeItem } from '../../lib/orgTreeApi';
import { AccessList } from '../people/AccessList';
import { SiteDevices, SiteSettings } from './SiteTabs';
import { leafText, levelText } from '../../lib/levels';
import { structCount, structTotalsText } from '../../lib/orgTreeUi';
import { AssignDialog, type AssignTarget } from './AssignDialog';
import { PreviewDialog } from './PreviewDialog';
import { BATCH_MAX, StructureTree, rootIdOf, useRootLevel, type SelNode } from './StructureTree';
import { BatchManagerDialog, HomeDialog, NodeDialog, PickNodeDialog } from './StructureDialogs';
import { useTreeRefresh, useTreeSettings } from './hooks';
import { otErr } from './otErr';
import { OwnerTransferDialog } from '../settings/OwnerTransferDialog';
import { HomeDetail } from './HomeDetail';

type Flow =
  | { kind: 'add-node' | 'rename' }
  | { kind: 'add-home' }
  | { kind: 'move-pick' }
  | { kind: 'move'; to: SelNode }
  | { kind: 'homes-pick' }
  | { kind: 'homes'; to: SelNode }
  | { kind: 'manager' }
  | { kind: 'block'; enabled: boolean }
  | null;

/** 頁首範圍 → 這頁的選取節點（同一個組織的場域 / 組織樹節點才有；組織本身 = 整個組織 = null）。 */
export function scopeSel(scope: string, orgId: string, tree: ScopeNode[]): SelNode | null {
  // 頁首範圍是一個家戶（「EddyStudio › lstw › lab」的 lab）：選它所在的節點（第四輪 R4-P1-4；直接掛在組織下就是整個組織）
  if (scope.startsWith('home:')) {
    const path = nodePath(tree, scope);
    const up = [...path.slice(0, -1)].reverse().find((n) => n.kind === 'site' || n.key.startsWith('node:' + orgId + ':'));
    return up ? scopeSel(up.key, orgId, tree) : null;
  }
  let id = '';
  if (scope.startsWith('node:' + orgId + ':')) id = scope.slice(('node:' + orgId + ':').length);
  else if (scope.startsWith('site:')) {
    const n = tree.find((x) => x.key === scope);
    // 範圍樹的 site 不帶組織；只認這個組織底下的（祖先是 org:<orgId>）
    if (!n) return null;
    const idx = tree.indexOf(n);
    for (let i = idx - 1; i >= 0; i--) if (tree[i].kind === 'org') {
      if (tree[i].key !== 'org:' + orgId) return null;
      break;
    }
    id = scope.slice(5);
  }
  if (!id) return null;
  const n = tree.find((x) => x.key === scope);
  return { node_id: id, name: n?.label ?? '', level_label: null, isRoot: false };
}

export function StructurePage({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const admin = isOrgAdmin(org);
  const sites = hasOrgPerm(org, 'org.sites');
  const settings = useTreeSettings(org);
  const root = useRootLevel(org);
  // 後端 subtree 還沒分開通道時，樹頂說明加一句「目前含 NVR 通道」（R2-P1-11）
  const inclChannels = structCount(root.data?.root?.subtree)?.inclChannels ?? false;
  const rootId = rootIdOf(root.data);
  const refresh = useTreeRefresh(org);
  // 打開時預先選頁首範圍所在的節點（第三輪 R3-P1-1：頁首是 EddyStudio › lab，這頁卻選 EddyStudio）
  const { scope, tree: scopeTree, homes: myHomes } = useScope();
  const [sel, setSel] = useState<SelNode | null>(() => scopeSel(scope, org.org_id, scopeTree));
  // 樹上點選的戶（跟節點互斥；選取模式時不能點）
  const [selHome, setSelHome] = useState<OrgTreeItem | null>(null);
  const [flow, setFlow] = useState<Flow>(null);
  const cur: SelNode | null = sel ?? (root.data?.root ? { node_id: rootId, name: t('ot.whole_org_name', { org: org.display_name }), level_label: null, isRoot: true } : null);
  const node = useQuery({ queryKey: ['ot', org.org_id, 'node', cur?.node_id], queryFn: () => treeApi.node(org, cur!.node_id), enabled: !!cur?.node_id, retry: false });
  // 〔＋ 戶〕對話框要「目前會涵蓋這一戶的指派摘要」（o30）：開對話框時才問
  const access = useQuery({ queryKey: ['ot', org.org_id, 'access', cur?.node_id], queryFn: () => treeApi.access(org, cur!.node_id), enabled: !!cur?.node_id && flow?.kind === 'add-home', retry: false });
  const [checked, setChecked] = useState<Map<string, OrgTreeItem>>(new Map());
  const [selecting, setSelecting] = useState(false);
  const [sp, setSp] = useSearchParams();
  const tab = (['devices', 'access', 'settings'] as const).find((x) => x === sp.get('tab')) ?? 'access';
  const setTab = (k: string) => setSp((p) => (p.set('tab', k), p), { replace: true });
  const [assign, setAssign] = useState<{ target: AssignTarget; existing?: Assignment } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [transfer, setTransfer] = useState<OrgTreeItem | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  const n = node.data;
  const homeParent = useQuery({
    queryKey: ['ot', org.org_id, 'node', selHome?.parent_node_id],
    queryFn: () => treeApi.node(org, selHome!.parent_node_id!),
    enabled: !!selHome?.parent_node_id,
    retry: false,
  });
  const isRoot = !!cur?.isRoot || (!!n && !n.parent_node_id);
  const name = isRoot ? t('ot.whole_org_name', { org: org.display_name }) : (n?.name ?? cur?.name ?? '');
  const checkedList = useMemo(() => [...checked.values()], [checked]);
  const pathText = [t('ot.whole_org_short', { org: org.display_name }), ...(n?.path ?? []).filter((p) => p.node_id !== rootId).map((p) => p.name), ...(isRoot ? [] : [name])].join(' › ');
  const allUnassigned = checkedList.every((h) => h.management_status === 'unassigned');

  function done(msg?: string) {
    setFlow(null);
    if (msg) setNotice(msg);
    refresh();
  }
  function moved(_r: unknown, okMsg: string) {
    done(okMsg);
  }

  async function archive() {
    if (!n) return;
    if (!(await ask({ danger: true, title: t('ot.archive_title', { name }), body: t('ot.archive_body'), action: t('ot.archive') }))) return;
    try {
      await treeApi.archiveNode(org, n.node_id, n.version);
      setSel(null);
      done(t('ot.archived', { name }));
    } catch (e) {
      setErr(otErr(t, e));
    }
  }

  // 戶的路徑：組織名 › 所在節點的上層 › 所在節點（所在節點是根 = 只寫組織名）
  const hp = homeParent.data;
  const homePathText = [
    t('ot.whole_org_short', { org: org.display_name }),
    ...(hp && hp.parent_node_id ? [...(hp.path ?? []).filter((p) => p.node_id !== rootId).map((p) => p.name), hp.name] : []),
  ].join(' › ');
  const canDeleteHome = !!selHome && (admin || myHomes.some((h) => h.home_id === selHome.home_id && h.role === 'owner'));
  function homeDeleted(homeName: string) {
    const parent = selHome?.parent_node_id;
    setSelHome(null);
    setErr(null);
    if (parent) setSel({ node_id: parent, name: hp?.name ?? '', level_label: hp?.level_label ?? null, isRoot: parent === rootId || (!!hp && !hp.parent_node_id) });
    done(t('ot.home_deleted', { name: homeName }));
  }

  const nodeTarget = (): AssignTarget => ({ kind: 'node', nodeId: cur!.node_id, nodeName: name, subtree: n?.subtree });
  const leaf = leafText(t, settings.data, org.kind === 'personal');

  return (
    <section className="ot-structure" data-testid="org-structure">
      <div className="toolbar wrap">
        <span className="spacer" />
        {selecting ? (
          <button className="btn btn-sm" onClick={() => (setSelecting(false), setChecked(new Map()))}>
            {t('ot.select_done')}
          </button>
        ) : (
          <button className="btn btn-sm" onClick={() => setSelecting(true)} data-testid="select-mode">
            {admin ? t('ot.batch_mode') : t('ot.batch_mode_move')}
          </button>
        )}
      </div>
      {notice && (
        <p className="notice card" role="status">
          {notice}{' '}
          <button className="link-btn" onClick={() => setNotice(null)}>
            {t('common.close')}
          </button>
        </p>
      )}
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="ot-split">
        <aside className="ot-left card">
          <div className="small muted ot-left-head" title={t('ot.struct_count_head')} data-testid="struct-count-head">
            {t('ot.struct_count_head')}
            {inclChannels && t('ot.struct_count_head_incl')}
          </div>
          <StructureTree
            org={org}
            settings={settings.data}
            selected={selHome ? undefined : cur?.node_id}
            onSelect={(s) => (setSel(s), setSelHome(null), setErr(null))}
            onPickHome={selecting ? undefined : (h) => (setSelHome(h), setErr(null), setNotice(null))}
            selectedHome={selecting ? undefined : selHome?.home_id}
            checked={checked}
            onCheck={
              selecting
                ? (it, on) => {
                    const m = new Map(checked);
                    if (on) {
                      if (m.size >= BATCH_MAX) return setErr(t('ot.batch_max', { n: BATCH_MAX }));
                      m.set(it.home_id!, it);
                    } else m.delete(it.home_id!);
                    setChecked(m);
                  }
                : undefined
            }
            showLock={admin}
            showUnassigned={sites}
          />
          {selecting && (
            <div className="ot-batchbar" data-testid="batch-bar">
              <span>{checked.size > 0 ? t('ot.n_selected', { n: checked.size, leaf }) : t('ot.select_hint', { leaf })}</span>
              <button className="btn btn-sm btn-primary" disabled={!checked.size} onClick={() => setFlow({ kind: 'homes-pick' })}>
                {t('ot.move_to')}
              </button>
              {admin && (
                <button className="btn btn-sm" disabled={!checked.size || !allUnassigned} title={allUnassigned ? undefined : t('ot.only_unassigned')} onClick={() => setFlow({ kind: 'manager' })}>
                  {t('ot.assign_manager')}
                </button>
              )}
              {admin && checkedList.length === 1 && checkedList[0].management_status !== 'unassigned' && (
                <button className="btn btn-sm" onClick={() => setTransfer(checkedList[0])}>
                  {t('transfer.button')}
                </button>
              )}
              <button className="link-btn small" onClick={() => (setSelecting(false), setChecked(new Map()))}>
                {t('common.cancel')}
              </button>
              {checked.size > 0 && !allUnassigned && admin && <span className="muted small">{t('ot.only_unassigned')}</span>}
            </div>
          )}
        </aside>
        <div className="ot-detail card" data-testid="node-detail">
          {selHome ? (
            <HomeDetail
              key={selHome.home_id}
              org={org}
              home={selHome}
              leaf={leaf}
              pathText={homePathText}
              showUnassigned={sites}
              canDelete={canDeleteHome}
              onDeleted={homeDeleted}
              onError={(m, gone) => {
                setErr(m);
                if (gone) refresh();
              }}
            />
          ) : !cur ? (
            <p className="muted">{root.isLoading ? t('common.loading') : t('ot.pick_node')}</p>
          ) : (
            <>
              <div className="row">
                <h3 className="ot-node-title">{name}</h3>
                {n?.level_label && <span className="ot-chip">{levelText(t, n.level_label)}</span>}
              </div>
              <p className="muted small" data-testid="node-summary">
                {pathText}
                {n?.subtree && ' · ' + structTotalsText(t, n.subtree)}
              </p>
              <div className="row">
                <button className="btn btn-sm" onClick={() => setFlow({ kind: 'add-node' })}>
                  {t('ot.add_child')}
                </button>
                <button className="btn btn-sm" onClick={() => setFlow({ kind: 'add-home' })}>
                  {t('ot.add_home', { leaf })}
                </button>
                {!isRoot && (
                  <>
                    <button className="btn btn-sm" onClick={() => setFlow({ kind: 'rename' })}>
                      {t('ot.rename_level')}
                    </button>
                    <button className="btn btn-sm" onClick={() => setFlow({ kind: 'move-pick' })}>
                      {t('ot.move')}
                    </button>
                  </>
                )}
              </div>
              <div className="tabs site-tabs" role="tablist">
                {(['devices', 'access', 'settings'] as const).map((k) => (
                  <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : undefined} onClick={() => setTab(k)}>
                    {t('ot.site_tab_' + k)}
                  </button>
                ))}
              </div>
              {tab === 'devices' && <SiteDevices org={org} nodeId={cur.node_id} isRoot={isRoot} name={name} pathText={pathText} />}
              {tab === 'access' && (
                <div data-testid="node-access">
                  <p className="muted small">{t('ot.site_access_hint')}</p>
                  <p className="notice" data-testid="site-access-scope">{t('iam.site_scope_help', { scope: isRoot ? name : pathText })}</p>
                  <AccessList key={cur.node_id} org={org} node={{ node_id: cur.node_id, name }} grantPreset={{ node: { node_id: cur.node_id, name, subtree: n?.subtree, isRoot } }} compact />
                </div>
              )}
              {tab === 'settings' && (
                <SiteSettings
                  org={org}
                  node={n}
                  isRoot={isRoot}
                  canEdit={sites}
                  onSaved={(m) => done(m)}
                  maintenance={
                    !isRoot && n && (admin || sites) ? (
                      <div className="maint-zone" data-testid="maintenance">
                        <h4>{t('ot.maint_title')}</h4>
                        {admin && (
                          <div className="maint-row" data-testid="block-toggle">
                            <div>
                              <strong>{t('ot.block_short')}</strong>
                              <div className="muted small">{t('ot.block_label')}</div>
                            </div>
                            <label className="row-inline">
                              <input type="checkbox" checked={!!n.block_inheritance} onChange={(e) => setFlow({ kind: 'block', enabled: e.target.checked })} />
                              {n.block_inheritance ? t('ot.block_is_on') : t('ot.block_is_off')}
                            </label>
                          </div>
                        )}
                        {sites && (
                          <div className="maint-row">
                            <div>
                              <strong>{t('ot.archive')}</strong>
                              <div className="muted small">{t('ot.archive_desc')}</div>
                            </div>
                            <button className="btn btn-sm btn-danger-outline" onClick={() => void archive()}>
                              {t('ot.archive')}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null
                  }
                />
              )}
            </>
          )}
        </div>
      </div>

      {cur && (flow?.kind === 'add-node' || flow?.kind === 'rename') && (
        <NodeDialog org={org} settings={settings.data} mode={flow.kind} parent={cur} node={n} close={() => setFlow(null)} done={(m) => done(m)} />
      )}
      {cur && flow?.kind === 'add-home' && (
        <HomeDialog
          org={org}
          at={{ node_id: cur.node_id, name, isRoot }}
          access={access.data}
          leaf={leafText(t, settings.data, org.kind === 'personal')}
          close={() => setFlow(null)}
          done={(m) => done(m)}
          onAssign={() => (setFlow(null), setAssign({ target: nodeTarget() }))}
        />
      )}
      {cur && n && flow?.kind === 'move-pick' && (
        <PickNodeDialog org={org} title={t('ot.move_node_title', { name })} exclude={n.node_id} close={() => setFlow(null)} pick={(to) => setFlow({ kind: 'move', to })} />
      )}
      {cur && n && flow?.kind === 'move' && (
        <PreviewDialog
          open
          title={t('ot.move_node_confirm', { name, to: flow.to.name })}
          intro={t('ot.move_node_intro')}
          target={name}
          load={() => treeApi.previewNode(org, n.node_id, { parent_node_id: flow.to.node_id })}
          submit={(p) => treeApi.updateNode(org, n.node_id, { parent_node_id: flow.to.node_id, expected_version: n.version, preview_token: p.token, also_revoke: p.alsoRevoke, reason: p.reason })}
          action={t('ot.move_action')}
          selfAction={t('ot.move_self_action')}
          onDone={(r) => moved(r, t('ot.moved', { name }))}
          onClose={() => setFlow(null)}
        />
      )}
      {flow?.kind === 'homes-pick' && <PickNodeDialog org={org} title={t('ot.move_homes_title', { count: checked.size })} close={() => setFlow(null)} pick={(to) => setFlow({ kind: 'homes', to })} />}
      {flow?.kind === 'homes' && (
        <PreviewDialog
          open
          title={t('ot.move_homes_confirm', { count: checked.size, to: flow.to.name })}
          intro={t('ot.move_homes_intro')}
          target={flow.to.name}
          load={() => (checkedList.length === 1 ? treeApi.previewHome(org, checkedList[0].home_id!, flow.to.node_id) : treeApi.previewHomes(org, checkedList.map((h) => h.home_id!), flow.to.node_id))}
          submit={(p) =>
            checkedList.length === 1
              ? treeApi.moveHome(org, checkedList[0].home_id!, { node_id: flow.to.node_id, expected_version: checkedList[0].version, preview_token: p.token, also_revoke: p.alsoRevoke, reason: p.reason })
              : treeApi.moveHomes(org, {
                  home_ids: checkedList.map((h) => h.home_id!),
                  node_id: flow.to.node_id,
                  expected_versions: Object.fromEntries(checkedList.map((h) => [h.home_id!, h.version])),
                  preview_token: p.token,
                  also_revoke: p.alsoRevoke,
                  reason: p.reason,
                }).catch((e) => {
                  // 任何一戶不合整批不動：detail.home_ids 列出
                  if (isKinioError(e) && Array.isArray(e.detail?.home_ids)) {
                    const bad = (e.detail!.home_ids as string[]).map((id) => checked.get(id)?.name ?? id).join('、');
                    throw Object.assign(e, { message: bad });
                  }
                  throw e;
                })
          }
          action={t('ot.move_homes_action', { count: checked.size })}
          selfAction={t('ot.move_self_action')}
          onDone={(r) => {
            setChecked(new Map());
            moved(r, t('ot.homes_moved', { count: checkedList.length }));
          }}
          onClose={() => setFlow(null)}
        />
      )}
      {flow?.kind === 'manager' && <BatchManagerDialog org={org} homes={checkedList} close={() => setFlow(null)} done={(m) => (setChecked(new Map()), done(m))} />}
      {cur && n && flow?.kind === 'block' && (
        <PreviewDialog
          open
          title={flow.enabled ? t('ot.block_on_title', { name }) : t('ot.block_off_title', { name })}
          intro={flow.enabled ? t('ot.block_on_intro', { name, homes: n.subtree?.homes ?? 0, devices: n.subtree?.devices ?? 0 }) : t('ot.block_off_intro', { name })}
          target={name}
          load={() => treeApi.previewNode(org, n.node_id, { block_inheritance: flow.enabled })}
          submit={(p) => treeApi.blockInheritance(org, n.node_id, { enabled: flow.enabled, expected_version: n.version, preview_token: p.token, also_revoke: p.alsoRevoke, reason: p.reason })}
          action={flow.enabled ? t('ot.block_on_action') : t('ot.block_off_action')}
          actionWithRevoke={(k) => t('ot.block_on_action_revoke', { n: k })}
          selfAction={t('ot.block_off_self_action')}
          note={flow.enabled ? t('ot.block_on_note') : undefined}
          onDone={() => done(flow.enabled ? t('ot.blocked_done', { name }) : t('ot.unblocked_done', { name }))}
          onClose={() => setFlow(null)}
        />
      )}
      <AssignDialog org={org} target={assign?.target ?? null} existing={assign?.existing} close={() => setAssign(null)} done={() => refresh()} />
      <OwnerTransferDialog
        org={org}
        home={
          transfer
            ? {
                home_id: transfer.home_id!,
                display_name: transfer.name,
                site_id: transfer.parent_node_id ?? '',
                owner_user_id: transfer.owner_user_id,
                management_status: transfer.management_status ?? '',
                env: org.env,
                things: transfer.devices ?? 0,
                version: transfer.version,
              }
            : null
        }
        close={() => (setTransfer(null), refresh())}
      />
      {dialog}
    </section>
  );
}
